/** Names a design or media edit for the undo and redo toasts. */
import {
  ColorsSchema,
  DisclaimerSchema,
  IconsSchema,
  LauncherSchema,
  LogoSchema,
  MediaAdjustSchema,
  ProductTilesSchema,
  StylesSchema,
  TextSchema,
  TypographySchema,
} from '@vwam/config-contract'
import type {
  DefaultsSource,
  LabelledConfig,
  LabelledPatch,
  MediaSpec,
} from './designHistoryLabels.types'
import type { BuilderDesignConfigPatch } from './types'

export const AI_CHANGE_LABEL = 'AI change'

const SECTION_LABELS: Record<keyof BuilderDesignConfigPatch, string> = {
  colors: 'Colors',
  typography: 'Typography',
  styles: 'Style',
  productTiles: 'Product tiles',
  icons: 'Icons',
  disclaimer: 'Disclaimer',
  logo: 'Logo',
  text: 'Text',
  avatar: 'Avatar',
  sendButton: 'Send button',
  background: 'Background',
  launcher: 'Launcher',
}

const PATH_LABELS: Record<string, string> = {
  'colors.primary': 'Primary Color',
  'colors.primaryText': 'Primary Text Color',
  'colors.background': 'Background Color',
  'colors.accent': 'Customer Bubble Color',
  'colors.accentText': 'Customer Bubble Text Color',
  'colors.bubbleColor': 'Assistant Bubble Color',
  'colors.systemText': 'Assistant Text Color',
  'colors.cardText': 'Card & Input Text Color',
  'typography.fontFamily': 'Font',
  'typography.fontSize': 'Font Size',
  'typography.lineHeight': 'Line Height',
  'logo.titleText': 'Title Text',
  'logo.alignment': 'Title Bar Alignment',
  'logo.titleBarSize': 'Title Bar Size',
  'logo.titleBarStyle': 'Title Bar Style',
  'logo.opacity': 'Logo Opacity',
  'logo.fit': 'Logo Fit',
  'text.loadingText': 'Loading Text',
  'text.inputPlaceholder': 'Input Placeholder',
  'disclaimer.text': 'Disclaimer',
  'productTiles.size': 'Products Size',
  'productTiles.contentAlignment': 'Products Alignment',
  'icons.avatarSize': 'Avatar Size',
  'styles.responseStyle': 'Response Style',
  'avatar.opacity': 'Avatar Opacity',
  'avatar.fit': 'Avatar Fit',
  'sendButton.opacity': 'Send Button Opacity',
  'sendButton.fit': 'Send Button Fit',
  'background.opacity': 'Background Image Opacity',
  'background.fit': 'Background Image Fit',
  'launcher.backgroundColor': 'Launcher Color',
  'launcher.borderRadius': 'Launcher Corner Radius',
  'launcher.position': 'Launcher Position',
  'launcher.offsetX': 'Launcher Horizontal Offset',
  'launcher.offsetY': 'Launcher Vertical Offset',
  'launcher.zIndex': 'Launcher Z-Index',
  'launcher.teaserText': 'Teaser Text',
  'launcher.showUnreadIndicator': 'Unread Indicator',
}

// Controls write these alongside the field they edit, such as showLogo with the title bar
// size, so they never name an edit on their own.
const COMPANION_PATHS = new Set(['logo.showLogo', 'disclaimer.enabled'])
const FALLBACK_LABEL = 'Settings'
const THEME_OWNED_SECTIONS = new Set(['colors', 'typography', 'styles'])

// The detailed response nests every image as an object; only the launcher id is also flat.
export const MEDIA: MediaSpec[] = [
  { field: 'logoAssetId', asset: 'logoAsset', label: 'Logo Image' },
  { field: 'faviconAssetId', asset: 'faviconAsset', label: 'Favicon' },
  {
    field: 'aiMessengerIconAssetId',
    asset: 'aiMessengerIconAsset',
    label: 'Avatar Image',
  },
  {
    field: 'sendButtonIconAssetId',
    asset: 'sendButtonIconAsset',
    label: 'Send Button Icon',
  },
  {
    field: 'backgroundImageAssetId',
    asset: 'backgroundImageAsset',
    label: 'Background Image',
  },
  { field: 'launcherAvatarAssetId', label: 'Launcher Avatar' },
]

const SECTION_SCHEMAS: Record<string, DefaultsSource> = {
  colors: ColorsSchema,
  typography: TypographySchema,
  styles: StylesSchema,
  productTiles: ProductTilesSchema,
  icons: IconsSchema,
  disclaimer: DisclaimerSchema,
  logo: LogoSchema,
  text: TextSchema,
  avatar: MediaAdjustSchema('contain'),
  sendButton: MediaAdjustSchema('contain'),
  background: MediaAdjustSchema('cover'),
  launcher: LauncherSchema,
}

type Leaves = Map<string, unknown>

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function schemaDefaults(section: string): Record<string, unknown> {
  const parsed = SECTION_SCHEMAS[section]?.safeParse({})

  return parsed?.success && isRecord(parsed.data) ? parsed.data : {}
}

// Overlays saved values on contract defaults, so a first edit that only writes a default is no change.
function withDefaults(design: unknown): Record<string, unknown> {
  const raw = isRecord(design) ? design : {}
  const filled = Object.keys(SECTION_SCHEMAS).flatMap((section) => {
    const value = raw[section]

    return value === undefined || isRecord(value)
      ? [[section, { ...schemaDefaults(section), ...value }]]
      : []
  })

  return { ...raw, ...Object.fromEntries(filled) }
}

function collectLeaves(value: unknown, prefix: string, into: Leaves): Leaves {
  if (!isRecord(value)) return into

  for (const [key, child] of Object.entries(value)) {
    const path = prefix ? `${prefix}.${key}` : key

    if (isRecord(child)) collectLeaves(child, path, into)
    else into.set(path, child)
  }

  return into
}

function leafKey(value: unknown): string {
  return typeof value === 'object' && value !== null
    ? JSON.stringify(value)
    : String(value)
}

function changedPaths(before: Leaves, after: Leaves): string[] {
  const paths = new Set([...before.keys(), ...after.keys()])

  return [...paths].filter(
    (path) => leafKey(before.get(path)) !== leafKey(after.get(path))
  )
}

function changedDesignPaths(before: unknown, after: unknown): string[] {
  return changedPaths(
    collectLeaves(withDefaults(before), '', new Map()),
    collectLeaves(withDefaults(after), '', new Map())
  )
}

const SECTION_NAMES = new Map<string, string>(Object.entries(SECTION_LABELS))

// A flat id only appears in the optimistic cache, where it is the newest value.
export function mediaId(config: LabelledConfig | undefined, spec: MediaSpec) {
  const flat = config?.[spec.field]

  if (flat !== undefined) return flat

  return (spec.asset ? config?.[spec.asset]?.id : undefined) ?? null
}

function joinNames(names: string[]): string {
  const [first, second] = names

  if (first === undefined) return ''

  if (second === undefined) return first

  if (names.length === 2) return `${first} and ${second}`

  return `${first}, ${second} and ${names.length - 2} more`
}

function designNames(changed: string[]): string[] {
  const hasTheme = changed.includes('styles.theme')
  const names = new Set<string>(hasTheme ? ['Theme'] : [])

  for (const path of changed) {
    const section = path.split('.')[0] ?? ''

    if (COMPANION_PATHS.has(path)) continue

    if (hasTheme && THEME_OWNED_SECTIONS.has(section)) continue

    const name = PATH_LABELS[path] ?? SECTION_NAMES.get(section)

    if (name) names.add(name)
  }

  return [...names]
}

function patchNames(patch: LabelledPatch | undefined): string[] {
  const design = patch?.designConfig
  const sections = (isRecord(design) ? Object.keys(design) : []).flatMap(
    (key) => SECTION_NAMES.get(key) ?? []
  )
  const media = MEDIA.filter(({ field }) => patch?.[field] !== undefined).map(
    ({ label }) => label
  )

  return [...new Set([...sections, ...media])]
}

function changeNames(
  before: LabelledConfig | undefined,
  after: LabelledConfig | undefined
): string[] {
  const media = MEDIA.filter(
    (spec) => mediaId(before, spec) !== mediaId(after, spec)
  ).map(({ label }) => label)

  return [
    ...designNames(
      changedDesignPaths(before?.designConfig, after?.designConfig)
    ),
    ...media,
  ]
}

/** Names the fields that differ between two saved configs, or undefined when none do. */
export function describeChange(
  before: LabelledConfig | undefined,
  after: LabelledConfig | undefined
): string | undefined {
  const names = changeNames(before, after)

  return names.length > 0 ? joinNames(names) : undefined
}

/** Names the edit from the design fields that differ, falling back to the sections the patch touched. */
export function designChangeLabel(
  before: unknown,
  after: unknown,
  patch?: LabelledPatch
): string {
  return configChangeLabel(
    { designConfig: before },
    { designConfig: after },
    patch
  )
}

/** Names the design and media fields an edit changed, falling back to what the request patch touched. */
export function configChangeLabel(
  before: LabelledConfig | undefined,
  after: LabelledConfig,
  patch?: LabelledPatch
): string {
  return (
    describeChange(before, after) ??
    (joinNames(patchNames(patch)) || FALLBACK_LABEL)
  )
}
